/**
 * Manifest tech-tests.
 *
 * @remarks
 * Migré du legacy `tech-tests/manifest.ts`. Les `visibilityCondition` sont
 * traduites en `TabConfig.enabledWhen` (renommage du contrat v2). Le compte à
 * rebours de l'attempt en cours (`EvaluationDeadlineTimer`) est exposé via
 * `globalHeaderSlot` : il redevient visible sur tous les contextes, comme le
 * legacy, mais sans import direct dans le Header.
 */

import {
  Bookmark,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  FileText,
  HelpCircle,
  Lightbulb,
  List,
  MessageCircle,
  Send,
  StickyNote,
  Timer,
} from 'lucide-react';
import { CONTEXTS, lazyView, watchStoreEntry, type FeatureManifest } from '@/app/registry';
import { useTechTestsStore } from './stores';
import { techTestsRoutes } from './routes';

// ─── Visibility helpers ────────────────────────────────────────────────────

const evalHasPersonas = (): boolean => {
  const s = useTechTestsStore.getState();
  const test = s.tests.find((t) => t.id === s.selectedTestId);
  const evaluation = test?.evaluations.find((e) => e.id === s.selectedEvaluationId);
  return (evaluation?.personas?.length ?? 0) > 0;
};

const evalHasChecklist = (): boolean => {
  const s = useTechTestsStore.getState();
  const test = s.tests.find((t) => t.id === s.selectedTestId);
  const evaluation = test?.evaluations.find((e) => e.id === s.selectedEvaluationId);
  return (evaluation?.checklist?.length ?? 0) > 0;
};

// ─── Manifest ──────────────────────────────────────────────────────────────

export const techTestsManifest: FeatureManifest = {
  id: 'tech-tests',
  contexts: [CONTEXTS.TECH_TESTS_CATALOG, CONTEXTS.TECH_TESTS, CONTEXTS.TECH_TEST_EVALUATION],

  routes: [{ routes: techTestsRoutes, errorFallbackLabel: 'Tests' }],

  centerView: {
    [CONTEXTS.TECH_TESTS_CATALOG]: lazyView(
      () => import('./views/TechTestsCatalogView'),
      'TechTestsCatalogView',
    ),
    [CONTEXTS.TECH_TESTS]: lazyView(
      () => import('./views/TechTestsCenterView'),
      'TechTestsCenterView',
    ),
    [CONTEXTS.TECH_TEST_EVALUATION]: lazyView(
      () => import('./views/EvaluationDocumentViewerView'),
      'EvaluationDocumentViewerView',
    ),
  },

  panels: {
    left: {
      tabs: {
        [CONTEXTS.TECH_TESTS]: [
          {
            id: 'tests-list',
            label: 'Liste des tests',
            icon: ClipboardList,
            view: lazyView(() => import('./views/TechTestsListView'), 'TechTestsListView'),
          },
        ],
        [CONTEXTS.TECH_TEST_EVALUATION]: [
          {
            id: 'evaluation-documents',
            label: 'Documents',
            icon: FileText,
            view: lazyView(
              () => import('./views/EvaluationDocumentExplorerView'),
              'EvaluationDocumentExplorerView',
            ),
          },
          {
            id: 'evaluation-toc',
            label: 'Sommaire',
            icon: List,
            view: lazyView(() => import('./views/EvaluationTocView'), 'EvaluationTocView'),
          },
          {
            id: 'evaluation-bookmarks',
            label: 'Signets',
            icon: Bookmark,
            view: lazyView(
              () => import('./views/EvaluationBookmarksView'),
              'EvaluationBookmarksView',
            ),
          },
        ],
      },
    },
    right: {
      tabs: {
        [CONTEXTS.TECH_TESTS_CATALOG]: [
          {
            id: 'tips',
            label: 'Conseils',
            icon: Lightbulb,
            view: lazyView(() => import('./views/TipsView'), 'TipsView'),
          },
        ],
        [CONTEXTS.TECH_TESTS]: [
          {
            id: 'test-guide',
            label: 'Guide',
            icon: HelpCircle,
            view: lazyView(() => import('./views/TechTestsGuideView'), 'TechTestsGuideView'),
          },
        ],
        [CONTEXTS.TECH_TEST_EVALUATION]: [
          {
            id: 'submission',
            label: 'Soumettre',
            icon: Send,
            view: lazyView(
              () => import('./views/EvaluationSubmissionView'),
              'EvaluationSubmissionView',
            ),
          },
          {
            id: 'evaluation-checklist',
            label: 'Checklist',
            icon: CheckSquare,
            view: lazyView(
              () => import('./views/EvaluationChecklistView'),
              'EvaluationChecklistView',
            ),
            enabledWhen: evalHasChecklist,
          },
          {
            id: 'evaluation-chat',
            label: 'Discussion',
            icon: MessageCircle,
            view: lazyView(() => import('./views/EvaluationChatView'), 'EvaluationChatView'),
            enabledWhen: evalHasPersonas,
          },
          {
            id: 'evaluation-notes',
            label: 'Notes',
            icon: StickyNote,
            view: lazyView(() => import('./views/EvaluationNotesView'), 'EvaluationNotesView'),
          },
          {
            id: 'evaluation-pomodoro',
            label: 'Focus',
            icon: Timer,
            view: lazyView(
              () => import('./views/EvaluationPomodoroView'),
              'EvaluationPomodoroView',
            ),
          },
        ],
      },
    },
  },

  navigation: [
    {
      id: 'tech-tests',
      label: 'Test technique',
      icon: ClipboardCheck,
      to: '/tests',
    },
  ],

  panelRequirements: {
    [CONTEXTS.TECH_TESTS_CATALOG]: {
      left: { open: false },
      // Panneau droit masqué par défaut, révélable via le toggle header.
      right: { open: false, defaultActiveTab: 'tips' },
    },
    [CONTEXTS.TECH_TESTS]: {
      left: { open: true, defaultActiveTab: 'tests-list' },
      // Panneau droit masqué par défaut, révélable via le toggle header.
      right: { open: false, defaultActiveTab: 'test-guide' },
    },
    [CONTEXTS.TECH_TEST_EVALUATION]: {
      left: { open: true, defaultActiveTab: 'evaluation-documents' },
      right: { open: true, defaultActiveTab: 'submission' },
    },
  },

  // Compte à rebours de l'attempt en cours : slot header GLOBAL (visible sur
  // tous les contextes, pas seulement la zone tests). Il dérive lui-même
  // l'attempt actif depuis le store et s'auto-cache s'il n'y en a pas.
  globalHeaderSlot: lazyView(
    () => import('./components/EvaluationDeadlineTimer'),
    'EvaluationDeadlineTimer',
  ),

  watchStores: [
    // Le shell doit re-render quand la sélection évaluation change (visibilité
    // des tabs Checklist/Chat dépend de evalHasChecklist/evalHasPersonas).
    watchStoreEntry(useTechTestsStore, (s) => s.selectedEvaluationId),
  ],
};
